import React, {useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import Mention from 'components/molecules/Mentions/Mention/Mention';
import Box from 'components/atoms/Layout/Box/Box';
import PropTypes from 'prop-types';
import {useClamped, useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledEditorReadOnly from 'components/molecules/Editors/EditorReadOnly/EditorReadOnly.styles';
import {useExtensions} from 'helpers/hooks/editor';
import Typography from 'components/atoms/Text/Typography/Typography';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import {generateHTML, generateJSON} from '@tiptap/core';
import {useLinkNavigate} from 'helpers/hooks/links';

// Read-only needs no editor: generateHTML runs the same schema and gives identical markup.
const RichTextReadOnly = (props) => {
  const {
    content,
    mentions,
    disabled,
    extensions
  } = props;

  const contentEl = useRef(null);
  const navigate = useLinkNavigate();

  const [mentionEls, setMentionEls] = useState([]);

  const html = useMemo(() => {
    let html = utils.md2html(content ?? '');
    if (mentions) {
      html = utils.editorMentions(html, mentions, true);
    }

    return generateHTML(generateJSON(html, extensions), extensions);
  }, [content, mentions, extensions]);

  // mentions render through a node view, so renderHTML leaves empty placeholders to fill
  useLayoutEffect(() => {
    const selector = (mentions ?? [])
      .map((mention) => `mention-component-${utils.sha1(mention.trigger)}`)
      .join(',');

    if (contentEl.current && selector.length > 0) {
      const found = Array.from(contentEl.current.querySelectorAll(selector)).map((el) => {
        const mention = (mentions ?? []).find((mention) => {
          return el.tagName.toLowerCase() === `mention-component-${utils.sha1(mention.trigger)}`;
        });

        el.className = `mention-component mention-component-${mention?.trigger}`;

        return {
          el: el,
          mention: mention,
          id: el.getAttribute('data-id'),
          label: el.getAttribute('data-label')
        };
      });

      setMentionEls(found);
    } else {
      setMentionEls([]);
    }
  }, [html, mentions]);

  useEffect(() => {
    const el = contentEl.current;

    if (el) {
      const handleClick = (e) => {
        const link = e.target?.closest?.('a');

        if (link) {
          if (link?.href && !utils.isExternalLink(link.href)) {
            navigate({
              event: e,
              to: utils.cleanInternalLink(link.href, true),
              relative: 'path',
              resetSearchParams: true
            });

            e.preventDefault();
          }

          e.stopPropagation();
        }
      }

      const handleKeydown = (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
          e.target?.closest?.('a')?.click();
          e.preventDefault();
        }
      }

      el.addEventListener('click', handleClick);
      el.addEventListener('keydown', handleKeydown);

      return () => {
        el.removeEventListener('click', handleClick);
        el.removeEventListener('keydown', handleKeydown);
      }
    }
  }, [navigate]);

  useEffect(() => {
    contentEl.current?.querySelectorAll?.('a')?.forEach((linkEl) => {
      linkEl.tabIndex = disabled ? -1 : 0;
    });
  }, [html, disabled]);

  return <Box className="MuiTiptap-RichTextContent-root">
    <Box ref={contentEl}
         className="ProseMirror"
         dangerouslySetInnerHTML={{__html: html}}/>
    {mentionEls.map(({el, mention, id, label}, idx) => {
      return createPortal(<Mention data={mention?.data}
                                   id={id?.toString?.()}
                                   display={`${mention?.trigger}${label}`}
                                   readOnly={true}
                                   {...mention?.MentionProps}/>, el, `mention_${idx}`);
    })}
  </Box>
};

const EditorReadOnly = React.forwardRef((props, ref) => {
  const {
    mentions,
    disabled,
    extensions,
    isLoading,
    showTooltip,
    children,
    ExtensionProps,
    TooltipProps,
    ...innerProps
  } = useComponentProps(props, 'EditorReadOnly');

  const innerRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const alwaysTooltip = showTooltip === 'always';

  const [clamped, clampedProps] = useClamped(() => {
    return innerRef.current?.querySelector?.('.ProseMirror > *');
  }, {
    active: (!isLoading && showTooltip && !alwaysTooltip),
    onMouseOver: innerProps.onMouseOver,
    onTouchStart: innerProps.onTouchStart
  });

  const defaultExtensions = useExtensions({mentions, ...ExtensionProps});
  const extensionsMemo = useMemo(() => {
    return (extensions ?? []).concat(defaultExtensions
      .filter((ext1) => !(extensions ?? [])?.find((ext2) => ext1.name.toLowerCase() === ext2.name.toLowerCase())));
  }, [extensions, defaultExtensions]);

  const content = children?.toString();

  innerProps.className = utils.flattenClassName(innerProps.className);

  const renderReadOnly = () => {
    return <RichTextReadOnly content={content}
                             disabled={disabled}
                             mentions={mentions}
                             extensions={extensionsMemo}/>
  }

  const renderTooltip = () => {
    if (!isLoading && showTooltip && (alwaysTooltip || clamped)) {
      return <StyledEditorReadOnly {...innerProps}>
        {renderReadOnly()}
      </StyledEditorReadOnly>
    }
  }

  const render = (tooltip) => {
    if (isLoading) {
      return <Typography ref={innerRef} isLoading={true}>
        {content}
      </Typography>
    } else {
      return <StyledEditorReadOnly ref={innerRef} {...innerProps}
                                   {...(tooltip ? clampedProps : null)}>
        {renderReadOnly()}
      </StyledEditorReadOnly>
    }
  }

  if (showTooltip && !utils.isEmpty(content)) {
    return <Tooltip title={renderTooltip()}
                    placement="bottom"
                    {...TooltipProps}>
      {render(true)}
    </Tooltip>
  } else {
    return render(false);
  }
});

EditorReadOnly.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  mentions: PropTypes.array,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  showTooltip: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string
  ]),
  TooltipProps: PropTypes.object,
  ExtensionProps: PropTypes.object
};

export default EditorReadOnly;
